// The locked ORDER architecture and THE one ranking function that decides which
// widgets an ORDER section shows. Nothing else in the orb decides visibility.
import type { GearId } from '../../../olmps/ascension';
import type { OrbWidgetModel } from './types';

export const ORDER_ARCH: Record<GearId, { core: string; specialists: string[] }> = {
  orient: { core: 'atlas', specialists: ['telos', 'oracle'] },
  regulate: { core: 'poros', specialists: ['numa', 'moyri', 'hrmez'] },
  direct: { core: 'kyrs', specialists: ['krnos', 'tkne'] },
  execute: { core: 'metis', specialists: ['themis', 'janus'] },
  revise: { core: 'clio', specialists: ['argos', 'nemos'] },
};

/** Max specialists shown next to the core in one section. */
export const MAX_SPECIALISTS = 2;

/**
 * Rank (lower = more important); null = not eligible to show by itself.
 *  1 critical blocker · 2 active current-Step tool · 3 active SWAP · 4 pinned / invoked
 *  5 contextual recommendation (alerted or related) · 6 passive (has live data, nothing to say).
 */
export function rankOf(m: OrbWidgetModel, pinned: ReadonlySet<string>, invoked: ReadonlySet<string>): number | null {
  const s = m.signals ?? {};
  if (m.status === 'critical' || s.blocker) return 1;
  if (s.active && s.related) return 2;
  if (m.id === 'moyri' && s.active) return 3;
  if (pinned.has(m.id) || invoked.has(m.id)) return 4;
  if (m.empty) return null;
  if (s.alerted || s.related || m.status === 'attention') return 5;
  return null; // passive: reachable through the "more" control, never pushed into view
}

export interface SectionPick {
  core: OrbWidgetModel;
  /** Specialists to render (≤ MAX_SPECIALISTS), most important first. */
  shown: OrbWidgetModel[];
  /** The rest, for the compact secondary control. */
  more: OrbWidgetModel[];
}

export function selectSection(gear: GearId, models: Record<string, OrbWidgetModel>, pinned: ReadonlySet<string>, invoked: ReadonlySet<string>): SectionPick {
  const arch = ORDER_ARCH[gear];
  const ranked = arch.specialists
    .map((id, order) => ({ m: models[id], order }))
    .filter((x): x is { m: OrbWidgetModel; order: number } => !!x.m)
    .map((x) => ({ ...x, rank: rankOf(x.m, pinned, invoked) }));
  const eligible = ranked.filter((x) => x.rank != null).sort((a, b) => a.rank! - b.rank! || a.order - b.order);
  const shown = eligible.slice(0, MAX_SPECIALISTS).map((x) => x.m);
  const more = ranked.filter((x) => !shown.includes(x.m)).map((x) => x.m);
  return { core: models[arch.core], shown, more };
}
